import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useNuxtApp } from 'nuxt/app';
import { uuid4 } from '~~/common/utils';
import { setErrorNotification } from '~~/common/utils/helper';
import { isApiError } from '~~/common/utils/apiError';
import otqs from '~~/common/utils/objectToQueryString';
import { useUserStore } from '~~/common/stores/user';
import { useConfigStore } from '~~/common/stores/config';
import { useAuthenticationStore } from '~~/common/stores/authentication';
import { F_CATEGORY, F_EDUCATION_LEVEL, F_EMPLOYING_COMPANY, F_EMPLOYMENT_TIME, F_EMPLOYMENT_TYPE, F_JOB, F_JOB_NAME_SEARCH, F_LOCATION } from '~~/common/config/CareerFilter';
import { useNotificationStore } from '~~/common/stores/notification';
import type { DeepPartial, Schema } from '~~/common/types/api';

type AdvertisementModel = DeepPartial<Schema<'AdvertisementModel'>>;

interface CareerAdvertisementsApiResponse {
  items?: AdvertisementModel[];
  count?: number;
}

interface CareerAdvertisement extends Omit<AdvertisementModel, 'advertisementId' | 'category' | 'employmentType' | 'employmentTime'> {
  advertisementId?: number | string;
  isPlaceholder?: boolean;
  category?: string | string[];
  employmentType?: string | string[];
  employmentTime?: string | number[];
}

interface CareerAdvertisementsResult {
  itemCount: number;
  pageCount: number;
  currentPage: number;
  result: CareerAdvertisement[];
}

interface CareerFilterValue {
  id: number | string;
  value: string;
}

interface CareerFilter {
  name: string;
  values: Record<string, CareerFilterValue> | CareerFilterValue[] | string;
}

interface CareerFilterParsed {
  id: number;
  name: string;
}

/** The leading entry of a filter's `values`: the "nothing selected yet" option of the select. */
interface CareerFilterSelectEmptyOption {
  id: 0;
  name: string;
  title: true;
  [IS_CAREER_FILTER_EMPTY]: true;
}

interface CareerFilterSelectOption {
  id: CareerFilterValue['id'];
  name: CareerFilterValue['value'];
}

interface CareerFilterSelectEntry {
  id: string;
  name: string;
  values: Record<string, CareerFilterSelectEmptyOption | CareerFilterSelectOption>;
}

interface SavedHistory {
  query: string;
  page: number;
  offers: unknown;
}

interface FilterGroups {
  [key: string]: (string | null)[];
}

interface CareerApplicationPayload {
  file?: File | null;
  application?: unknown;
}


export const IS_CAREER_FILTER_EMPTY = 'isEmptySelection';
export const CAREER_FILTER_EDUCATIONLEVEL = 'EducationLevel';
export const CAREER_FILTER_EMPLOYMENTTIME = 'EmploymentTime';
export const CAREER_FILTER_EMPLOYMENTTYPE = 'EmploymentType';
export const CAREER_FILTER_CATEGORY = 'Category';

const CAREER_CV_REQUIRED = 'career_cv_required';

export const useCareerStore = defineStore('career', () => {
  const nuxtApp = useNuxtApp();
  const authenticationStore = useAuthenticationStore();
  // Filters are identity-dependent. Both caches store the `careerFiltersKey`
  // they belong to, so a stale entry from a previous identity is ignored (and
  // replaced) rather than accumulated once the key changes.
  const _careerFiltersCache = ref<{ key: string; result: CareerFilter[] } | null>(null);
  let _careerFiltersPromiseCache: { key: string; result: Promise<CareerFilter[]> } | null = null;
  const careerFiltersKey = computed(() => {
    const userData = useUserStore().data;
    const email = userData?.personalInfo?.email || '';
    return `common/stores/career_${email}`;
  });
  const _savedHistory = ref<SavedHistory | null>(null);
  const showCareerJobListPage = ref(false);

  function isCvRequired () {
    const config = useConfigStore();
    const configCommon = config?.configCommon as { persistentConfig?: Record<string, unknown> } | null;
    return configCommon?.persistentConfig?.[CAREER_CV_REQUIRED];
  }

  function saveHistory ({ query, page, offers }: SavedHistory) {
    _savedHistory.value = { query, page, offers };
  }

  function clearHistory () {
    _savedHistory.value = null;
  }

  function getHistory ({ query, page }: { query: string; page: number }) {
    const savedHistory = _savedHistory.value;
    return (query && savedHistory?.query === query && page <= savedHistory?.page
      ? savedHistory
      : null
    );
  }

  async function fetchCareerOffer ({ applicationGuid }: { applicationGuid?: string } = {}) {
    await authenticationStore.reInitToken();

    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      return await $api.$get<Schema<'OfferModel'>>(`/career/offers/by-application/${applicationGuid}`);
    } catch (error: unknown) {
      if (!isApiError(error) || error.response?.status !== 404) {
        $logger.error(error);
        setErrorNotification({ nuxtApp, error });
      }
      return (isApiError(error) && error.response?.status) || null;
    }
  }

  async function respondToCareerOffer ({
    isAccepted,
    applicationGuid,
    reason = '',
  }: {
    isAccepted?: boolean;
    applicationGuid?: string;
    reason?: string;
  } = {}) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      if (!applicationGuid || typeof reason !== 'string' || typeof isAccepted !== 'boolean') {
        throw new TypeError(`incorrect arguments ${applicationGuid},${reason},${isAccepted}`);
      }
      await $api.$post('/career/offer_respond', {
        applicationGuid,
        action: isAccepted ? 'accept' : 'decline',
        reason,
      });
      return [true, null];
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
      return [false, error];
    }
  }

  function generateEmptyCareerAdvertisements (itemCount = 0): CareerAdvertisementsResult {
    return {
      itemCount,
      pageCount: 1,
      currentPage: 1,
      result: new Array<CareerAdvertisement>(itemCount).fill({}).map(() => ({
        isPlaceholder: true,
        advertisementId: uuid4(),
      } satisfies CareerAdvertisement)),
    };
  }

  function createCareerAdvertisementsQuery ({
    page,
    itemsPerPage,
    filterGroups,
  }: {
    // null means "leave it out": objectToQueryString drops null values, and
    // callers pass it explicitly to build a query without paging.
    page?: number | null;
    itemsPerPage?: number | null;
    filterGroups?: FilterGroups;
  } = {}) {
    return `/career/advertisements${otqs({
      ...page !== undefined && { page },
      ...itemsPerPage !== undefined && { itemsPerPage },
      ...createOrderedGroupFilter({ filterGroups }),
    })}`;
  }

  async function fetchCareerAdvertisements (query: string): Promise<CareerAdvertisementsResult> {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {

      await authenticationStore.reInitToken();

      const parsedQuery = new URLSearchParams(
        (query.includes('?')
          ? query.slice(query.indexOf('?'))
          : ''
        ),
      );
      const [apiResult] = await Promise.all([
        $api.$get<CareerAdvertisementsApiResponse>(query),
        fetchCareerAdvertisementFilters(),
      ]);
      const result: CareerAdvertisementsApiResponse = apiResult;
      if (Array.isArray(result.items)) {
        const data = await Promise.all(result.items.map(async (advertisement) => {
          return {
            ...advertisement,
            category: (await Promise.all((advertisement.category ?? [])
              .map(async (category) => (await fetchCareerAdvertisementFiltersByNameAndValue(CAREER_FILTER_CATEGORY, category))?.name)))
              ?.join(),
            employmentType: (await Promise.all((advertisement.employmentType ?? [])
              .map(async (employmentType) => (await fetchCareerAdvertisementFiltersByNameAndValue(CAREER_FILTER_EMPLOYMENTTYPE, employmentType))?.name)))
              ?.join(),
            employmentTime: (await Promise.all((advertisement.employmentTime ?? [])
              .map(async (employmentTime) => (await fetchCareerAdvertisementFiltersByNameAndValue(CAREER_FILTER_EMPLOYMENTTIME, employmentTime))?.name)))
              ?.join(),
          };
        }));
        return {
          itemCount: result.count ?? 0,
          pageCount: Math.ceil((result.count ?? 0) / Number(parsedQuery.get('itemsPerPage'))),
          currentPage: Number(parsedQuery.get('page')),
          result: data,
        };
      } else {
        throw new TypeError('Invalid /career/advertisements received on API.');
      }
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
      return generateEmptyCareerAdvertisements(0);
    }
  }

  async function fetchCareerAdvertisementFilters (): Promise<CareerFilter[]> {
    const {
      $logger,
    } = nuxtApp;
    try {
      const key = careerFiltersKey.value;
      if (_careerFiltersCache.value?.key !== key) {
        if (_careerFiltersPromiseCache?.key !== key) {
          _careerFiltersPromiseCache = { key, result: _fetchUnCachedCareerAdvertisementFilters() };
        }
        _careerFiltersCache.value = { key, result: await _careerFiltersPromiseCache.result };
        _careerFiltersPromiseCache = null;
      }
      return _careerFiltersCache.value.result;
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
      return [];
    }
  }

  async function _fetchUnCachedCareerAdvertisementFilters (): Promise<CareerFilter[]> {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      await authenticationStore.reInitToken();
      const filtersResponse = await $api.$get<CareerFilter[]>('/career/filters');
      return filtersResponse.map(({
        name,
        values,
      }) => ({
        name,
        values,
      }));
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
      return [];
    }
  }

  async function fetchCareerAdvertisementById (id: string | number) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      await authenticationStore.reInitToken();

      return [await $api.$get<Schema<'AdvertisementModel'>>(`/career/advertisement/${id}`), null];
    } catch (error: unknown) {
      if (!isApiError(error) || error.response?.status !== 404) {
        $logger.error(error);
      }
      setErrorNotification({ nuxtApp, error });
      return [[], error];
    }
  }

  async function fetchCareerAdvertisementQuestionsById (id: string | number) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      await authenticationStore.reInitToken();
      return [await $api.$get<Schema<'QuestionModel'>[]>(`/career/advertisements/${id}/questions?page=1`), null];
    } catch (error: unknown) {
      if (!isApiError(error) || error.response?.status !== 404) {
        $logger.error(error);
      }
      setErrorNotification({ nuxtApp, error });
      return [[], error];
    }
  }

  async function fetchCareerAdvertisementFiltersByName (filterName: string): Promise<CareerFilterParsed[]> {
    const { $logger } = nuxtApp;

    try {
      const filters = (await fetchCareerAdvertisementFilters())?.find(filter => filter.name === filterName)?.values;

      if (!filters) {
        return [];
      }

      return Object.entries(filters)?.map(([, filter]) => {
        const filterValue: CareerFilterValue = typeof filter === 'object' ? filter : { id: filter, value: filter };
        return {
          id: parseInt(String(filterValue.id)),
          name: filterValue.value,
        };
      });
    } catch (err) {
      $logger.error(err);
      return [];
    }
  }

  async function fetchCareerAdvertisementFiltersByNameAndValue (filterName: string, id: unknown): Promise<CareerFilterParsed | undefined> {
    const { $logger } = nuxtApp;

    try {
      return (await fetchCareerAdvertisementFiltersByName(filterName))?.find(filter => filter.id === id);
    } catch (err) {
      $logger.error(err);
    }
  }

  // Pure, synchronous transform of the raw filters into the select-form shape.
  // Kept context-free (no fetching) so it can be driven by useAsyncData from a
  // composable; see useCareerFiltersSelectForm.
  function buildCareerFiltersSelectForm (filters: CareerFilter[]): Record<string, CareerFilterSelectEntry> {
    const { $awt } = nuxtApp;
    const filterNames: Record<string, string> = {
      [F_JOB_NAME_SEARCH]: $awt('aw.career_list.filter.job_name_search.label'),
      [F_CATEGORY]: $awt('aw.career_list.filter.category.label'),
      [F_LOCATION]: $awt('aw.career_list.filter.location.label'),
      [F_JOB]: $awt('aw.career_list.filter.job.label'),
      [F_EDUCATION_LEVEL]: $awt('aw.career_list.filter.education_level.label'),
      [F_EMPLOYING_COMPANY]: $awt('aw.career_list.filter.company.label'),
      [F_EMPLOYMENT_TIME]: $awt('aw.career_list.filter.employment_time.label'),
      [F_EMPLOYMENT_TYPE]: $awt('aw.career_list.filter.employment_type.label'),
    };
    return Object.fromEntries(filters.map((filter) => ([
      filter.name,
      {
        id: filter.name,
        name: filterNames[filter.name] || '',
        values: Object.fromEntries([
          [
            0,
            {
              id: 0,
              name: filterNames[filter.name],
              title: true,
              [IS_CAREER_FILTER_EMPTY]: true,
            },
          ],
          // WARNING backend sometimes sends filter.values as an
          // array and sometimes as an objects and sometimes as an
          // empty string, so Object.entries ensures that we always
          // iterate on an array with map.
          ...(Object.entries(filter.values)
            .map(([, {
              id,
              value,
            }]) => {
              return [id, {
                id,
                name: value,
              }];
            })
          ),
        ]),
      },
    ])));
  }

  async function createApplication ({ file, application }: CareerApplicationPayload) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      const formData = new FormData();
      if (application) {
        formData.append('application', JSON.stringify(application));
      }
      if (file) {
        formData.append('file', file);
      }
      await $api.$post('career/applications', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const notificationStore = useNotificationStore();
      notificationStore.pushSuccess({
        text: {
          titleToken: 'aw.pages.career.application_form.success_message',
        },
      });
      return [true, {}];

    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
      return [false, { error: error }];
    }
  }

  async function postViewRecommendation (hash: string) {
    const {
      $api,
      $logger,
    } = nuxtApp;
    try {
      await $api.$post(`career/view_recommendation/${hash}`, {});
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
  }

  function setShowCareerJobListPage (newValue: boolean) {
    showCareerJobListPage.value = newValue;
  }

  return {
    respondToCareerOffer,
    isCvRequired,
    fetchCareerOffer,
    generateEmptyCareerAdvertisements,
    createCareerAdvertisementsQuery,
    createOrderedGroupFilterReverse,
    fetchCareerAdvertisements,
    buildCareerFiltersSelectForm,
    _savedHistory,
    _careerFiltersCache,
    showCareerJobListPage,
    careerFiltersKey,
    fetchCareerAdvertisementFilters,
    fetchCareerAdvertisementById,
    fetchCareerAdvertisementQuestionsById,
    fetchCareerAdvertisementFiltersByName,
    fetchCareerAdvertisementFiltersByNameAndValue,
    createApplication,
    postViewRecommendation,
    saveHistory,
    clearHistory,
    getHistory,
    setShowCareerJobListPage,
  };
});

function createOrderedGroupFilter ({ filterGroups }: { filterGroups?: FilterGroups }) {
  // const filterGroups = {'kitten': [false], 'witch': [null], animal: ['dog', null, 'cat'], tree: [null, 'apple']};
  const filterKeyValues = (filterGroups && typeof filterGroups === 'object') ? filterGroups : {};
  const initialAcc: { filters: string[]; filterValues: string[] } = {
    filters: [],
    filterValues: [],
  };
  return Object.keys(filterKeyValues).sort().reduce(
    (acc, curr) => {
      const filtered = (
        (Array.isArray(filterKeyValues[curr])
          ? filterKeyValues[curr]
          : []
        )
          .filter((a): a is string => a !== null)
      );
      if (filtered.length) {
        acc.filters.push(...new Array(filtered.length).fill(curr));
        acc.filterValues.push(...filtered);
      }
      return acc;
    },
    initialAcc,
  );
}

function createOrderedGroupFilterReverse ({
  filters,
  filterValues,
}: {
  filters?: string[];
  filterValues?: string[];
} = {}) {
  if (!Array.isArray(filters) || !Array.isArray(filterValues)) {
    return {};
  }
  return filters.reduce<Record<string, string[]>>((acc, curr, idx) => {
    if (!acc[curr]) {
      acc[curr] = [];
    }
    if (filterValues[idx] !== undefined) {
      acc[curr].push(filterValues[idx]);
    }
    return acc;
  }, {});
}
